import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BarChart3, Shield, Bell, MessageSquare, Palette, ChevronRight, Building2, LogOut } from 'lucide-react';
import { useBranding } from '../contexts/BrandingContext';
import { useAuth } from '../contexts/AuthContext';
import Icon from './AppIcon';


export default function AdminSidebar() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const activeTab = searchParams?.get('tab') || 'dashboard';
  const { branding, loading } = useBranding();
  const { signOut, profile } = useAuth();
  const [loggingOut, setLoggingOut] = useState(false);

  const menuItems = [
    { icon: BarChart3, label: 'Dashboard', value: 'dashboard' },
    { icon: Shield, label: 'Manage Admins', value: 'admins' },
    { icon: Bell, label: 'Notifications', value: 'notifications' },
    { icon: MessageSquare, label: 'Support Tickets', value: 'support' },
    { icon: Palette, label: 'Branding', value: 'branding' },
  ];

  const handleTabChange = (tab) => {
    navigate(`/admin-dashboard?tab=${tab}`);
  };

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to logout?')) {
      try {
        setLoggingOut(true);
        await signOut();
        navigate('/admin-login', { replace: true });
      } catch (error) {
        console.error('Logout error:', error);
        alert('Failed to logout. Please try again.');
      } finally {
        setLoggingOut(false);
      }
    }
  };

  return (
    <div className="fixed left-0 top-0 h-full w-64 bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800 border-r border-purple-900 z-30 shadow-2xl flex flex-col">
      <div className="p-6 flex-1 overflow-y-auto">
        {!loading && (
          <div className="mb-6 pb-6 border-b border-purple-400 border-opacity-30">
            {branding?.logoUrl ? (
              <div className="flex justify-center">
                <img 
                  src={branding?.logoUrl} 
                  alt="Company Logo" 
                  className="h-12 w-auto object-contain"
                />
              </div>
            ) : branding?.brandName ? (
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Building2 className="w-5 h-5 text-purple-200" />
                  <h1 className="text-lg font-bold text-white">
                    {branding?.brandName}
                  </h1>
                </div>
                {branding?.slogan && (
                  <p className="text-sm text-purple-200">
                    {branding?.slogan}
                  </p>
                )}
              </div>
            ) : null}
          </div>
        )}

        <h2 className="text-lg font-semibold text-white mb-6">Admin Panel</h2>
        <nav className="space-y-2">
          {menuItems?.map((item) => {
            const Icon = item?.icon;
            const isActive = activeTab === item?.value;
            
            return (
              <button
                key={item?.value}
                onClick={() => handleTabChange(item?.value)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all duration-200 ${
                  isActive
                    ? 'bg-white bg-opacity-20 text-white shadow-lg backdrop-blur-sm' 
                    : 'text-purple-100 hover:bg-white hover:bg-opacity-10'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item?.label}</span>
                </div>
                {isActive && <ChevronRight className="w-4 h-4" />}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Admin Profile & Logout Section */}
      <div className="p-6 border-t border-purple-400 border-opacity-30 bg-black bg-opacity-20">
        {profile && (
          <div className="mb-4">
            <div className="flex items-center gap-3 mb-2">
              {profile?.avatarUrl ? (
                <img
                  className="h-10 w-10 rounded-full border-2 border-purple-300"
                  src={profile?.avatarUrl}
                  alt={profile?.fullName}
                />
              ) : (
                <div className="h-10 w-10 rounded-full bg-purple-400 flex items-center justify-center text-white font-semibold border-2 border-purple-300">
                  {profile?.fullName?.charAt(0)?.toUpperCase() || 'A'}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{profile?.fullName}</p>
                <p className="text-xs text-purple-200 truncate">{profile?.email}</p>
              </div>
            </div>
          </div>
        )}
        
        <button
          onClick={handleLogout}
          disabled={loggingOut}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
        >
          {loggingOut ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span className="font-medium">Logging out...</span>
            </>
          ) : (
            <>
              <LogOut className="w-4 h-4" />
              <span className="font-medium">Logout</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}